import type { FC } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { debounce } from '../../../utils/debounce';
import { IHelpComponentWrapper } from '../types';

import styles from './HelpComponentWrapper.module.css';

interface HelpComponentWrapperProps extends IHelpComponentWrapper {
  isOriginalHeight?: boolean;
  minWidth?: string;
  maxWidth?: string;
}

export const HelpComponentWrapper: FC<HelpComponentWrapperProps> = ({
  style,
  children,
  isOriginalHeight = true,
  minWidth,
  maxWidth,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const scaleComponent = useCallback(
    (childElement: HTMLElement): void => {
      if (!childElement) return;

      const containerWidth = containerRef.current?.offsetWidth;
      const childWidth = childElement.offsetWidth;

      if (containerWidth && childWidth > containerWidth) {
        const scale = containerWidth / childWidth;
        childElement.style.transform = `scale(${scale})`;
      } else {
        childElement.style.transform = 'none';
      }

      if (!isOriginalHeight) {
        const childHeight = childElement.getBoundingClientRect().height;
        containerRef.current!.style.height = `${childHeight}px`;
      }

      // This ensures that element resizes without flickering
      childElement.style.visibility = 'visible';
    },
    [isOriginalHeight]
  );

  const debounceScaleComponent = debounce(() => {
    if (containerRef.current) {
      const childElement = containerRef.current.children[0] as HTMLElement;
      scaleComponent(childElement);
    }
  }, 100);

  const overrideComponentMinWidth = useCallback(() => {
    if (minWidth && containerRef.current) {
      const childElement = containerRef.current.children[0] as HTMLElement;

      if (childElement) {
        childElement.style.minWidth = minWidth;
      }
    }
  }, [minWidth]);

  const overrideComponentMaxWidth = useCallback(() => {
    if (maxWidth && containerRef.current) {
      const childElement = containerRef.current.children[0] as HTMLElement;

      if (childElement) {
        childElement.style.maxWidth = maxWidth;
      }
    }
  }, [maxWidth]);

  useEffect(() => {
    const handleResize = (): void => {
      debounceScaleComponent();
      overrideComponentMinWidth();
      overrideComponentMaxWidth();
    };

    handleResize();
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [debounceScaleComponent, overrideComponentMinWidth, overrideComponentMaxWidth]);

  return (
    <div ref={containerRef} className={styles.help__componentWrapper} style={style}>
      {children}
    </div>
  );
};
